<template>
  <a-card :bordered="false" class="bank-panel bank-panel--accounts">
    <template #title>
      <div>
        <span>Connected accounts</span>
        <p class="bank-panel__subtitle">Stored bank accounts ready for review and transaction drill-down.</p>
      </div>
    </template>
    <template #extra>
      <span class="bank-panel__count">{{ accounts.length }} account<span v-if="accounts.length !== 1">s</span></span>
    </template>

    <div v-if="loading" class="bank-list__skeleton">
      <a-skeleton active :paragraph="{ rows: 4 }" />
    </div>

    <a-empty v-else-if="!accounts.length" description="No bank accounts connected yet." />

    <div v-else class="bank-list">
      <button
        v-for="account in accounts"
        :key="account.id"
        type="button"
        class="bank-list__item"
        :class="{ 'bank-list__item--active': selectedAccountId === account.id }"
        @click="$emit('select', account)"
      >
        <div class="bank-list__identity">
          <span class="bank-list__avatar">{{ getInitial(account.institution_name || account.institution_id || account.account_name || account.name) }}</span>
          <div>
            <p class="bank-list__institution">{{ account.institution_name || account.institution_id || 'Yapily Bank' }}</p>
            <h3>{{ account.account_name || account.name || 'Unnamed account' }}</h3>
            <p class="bank-list__meta">
              {{ account.account_type || 'Account' }}
              <span v-if="account.currency"> • {{ account.currency }}</span>
              <span v-if="account.account_id"> • {{ formatReference(account.account_id) }}</span>
            </p>
          </div>
        </div>
        <div class="bank-list__balance">
          <a-tag color="blue">{{ account.transactions_count || 0 }} txns</a-tag>
          <strong>{{ formatMoney(account.balance, account.currency) }}</strong>
        </div>
      </button>
    </div>
  </a-card>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'BankList',
  props: {
    accounts: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectedAccountId: {
      type: Number,
      default: null,
    },
  },
  emits: ['select'],
  setup() {
    const getInitial = (value) => {
      return (value || '?').toString().trim().charAt(0).toUpperCase();
    };

    const formatMoney = (value, currency) => {
      const amount = Number(value || 0);

      try {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: currency || 'GBP',
        }).format(amount);
      } catch (error) {
        return `${amount.toFixed(2)} ${currency || 'GBP'}`;
      }
    };

    const formatReference = (value) => {
      const stringValue = (value || '').toString();

      if (stringValue.length <= 6) {
        return stringValue;
      }

      return `Ref ${stringValue.slice(-6)}`;
    };

    return {
      getInitial,
      formatReference,
      formatMoney,
    };
  },
});
</script>

<style scoped>
.bank-panel {
  border: 1px solid #d8e2ec;
  border-radius: 22px;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
}

.bank-panel :deep(.ant-card-head) {
  border-bottom: 1px solid #eef3f8;
}

.bank-panel__subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7a8c;
}

.bank-panel__count {
  color: #64748b;
  font-weight: 600;
}

.bank-list {
  display: grid;
  gap: 14px;
}

.bank-list__item {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  border: 1px solid #dde6ef;
  border-radius: 18px;
  padding: 18px 20px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  text-align: left;
  transition: all 0.2s ease;
}

.bank-list__item:hover,
.bank-list__item--active {
  border-color: #0f766e;
  box-shadow: 0 18px 34px rgba(15, 118, 110, 0.12);
  transform: translateY(-1px);
}

.bank-list__identity {
  display: flex;
  align-items: center;
  gap: 14px;
}

.bank-list__avatar {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(135deg, #dcfce7 0%, #ccfbf1 100%);
  color: #0f766e;
  font-weight: 700;
  font-size: 16px;
}

.bank-list__institution,
.bank-list__meta {
  margin: 0;
  color: #64748b;
}

.bank-list__item h3 {
  margin: 4px 0;
  color: #0f172a;
  font-size: 18px;
}

.bank-list__balance {
  display: grid;
  justify-items: end;
  gap: 8px;
  color: #0f172a;
}

.bank-list__balance strong {
  font-size: 18px;
}

@media only screen and (max-width: 767px) {
  .bank-list__item {
    flex-direction: column;
    align-items: flex-start;
  }

  .bank-list__balance {
    justify-items: start;
  }
}
</style>